import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CLI_TOOLS, permissionsSchema, quotasSchema, type GroupInput, type Permissions } from "@krut/shared";
import { api, unwrap } from "../api.ts";
import { useGroups } from "../hooks.ts";
import { Badge, Button, Card, cx, Dialog, ErrorText, Field, Input, PageHeader, Spinner, Switch } from "../components/ui.tsx";

type Group = NonNullable<ReturnType<typeof useGroups>["data"]>[number];

// Defaults only — the name is empty until the admin types one, so don't validate the whole form here.
const blank = (): GroupInput => ({ name: "", description: "", quotas: quotasSchema.parse({}), permissions: permissionsSchema.parse({}), isDefault: false });

/** `later` = saved now, enforced when that feature ships (so admins can set policy ahead). */
const toggles: { key: keyof Permissions; label: string; hint: string; later?: string }[] = [
  { key: "terminal", label: "Terminal", hint: "Run shell commands in the user's sandbox" },
  { key: "codeExecution", label: "Code execution", hint: "Run Python snippets" },
  { key: "browser", label: "Browser", hint: "Drive a headless browser" },
  { key: "webTools", label: "Web search & fetch", hint: "Search the web and read pages" },
  { key: "cronJobs", label: "Scheduled jobs", hint: "Create recurring tasks" },
  { key: "subagents", label: "Subagents", hint: "Split work across helper agents — multiplies AI cost" },
  { key: "mediaGeneration", label: "Image & video generation", hint: "Paid media APIs" },
  { key: "voice", label: "Voice", hint: "Spoken replies (text-to-speech)" },
  { key: "fileUpload", label: "File uploads", hint: "Upload files into chat", later: "with the chat app" },
  { key: "promoteMemory", label: "Share memories", hint: "Memories may be promoted to company memory", later: "with company memory" },
  { key: "publishSkills", label: "Publish skills", hint: "Skills they create may be shared company-wide", later: "with the skills library" },
  { key: "colleagueRequests", label: "Colleague requests", hint: "Their assistant may ask colleagues' assistants for things, and take requests (owners approve anything shared)" },
  { key: "customMcp", label: "Own MCP servers", hint: "Add private MCP servers for their own assistant (company-wide servers are always available)" },
];

export function GroupsPage() {
  const groups = useGroups();
  const [editing, setEditing] = useState<Group | "new" | null>(null);
  return (
    <>
      <PageHeader
        title="Groups & permissions"
        description="Every user belongs to one group. The group sets their budget, resources and what their assistant may use."
        actions={<Button variant="primary" onClick={() => setEditing("new")}>New group</Button>}
      />
      {groups.isPending && <div className="grid place-items-center py-16"><Spinner /></div>}
      <div className="grid gap-4 sm:grid-cols-2">
        {groups.data?.map((g) => (
          <Card key={g.id} className="flex flex-col p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold">{g.name}</h3>
                  {g.isDefault && <Badge tone="accent">default</Badge>}
                </div>
                {g.description && <p className="mt-1 text-sm text-[var(--muted)]">{g.description}</p>}
              </div>
              <span className="text-sm text-[var(--muted)]">{g.members} {g.members === 1 ? "member" : "members"}</span>
            </div>
            <dl className="mt-5 grid grid-cols-3 gap-3 text-sm">
              <Stat label="Budget / mo" value={g.quotas.monthlyBudgetUsd === null ? "Unlimited" : `$${g.quotas.monthlyBudgetUsd}`} />
              <Stat label="Storage" value={`${g.quotas.storageGb} GB`} />
              <Stat label="Memory" value={`${(g.quotas.memoryMb / 1024).toFixed(g.quotas.memoryMb % 1024 ? 1 : 0)} GB`} />
            </dl>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {toggles.filter((t) => g.permissions[t.key] === true).map((t) => <Badge key={t.key}>{t.label}</Badge>)}
              {g.permissions.cliTools.map((c) => <Badge key={c} tone="accent">{c}</Badge>)}
            </div>
            <div className="mt-5 flex justify-end pt-1">
              <Button onClick={() => setEditing(g)}>Edit</Button>
            </div>
          </Card>
        ))}
      </div>
      {editing && <GroupDialog group={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-[var(--panel-2)] px-3 py-2">
      <dt className="text-xs text-[var(--muted)]">{label}</dt>
      <dd className="mt-0.5 font-medium">{value}</dd>
    </div>
  );
}

function GroupDialog({ group, onClose }: { group: Group | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<GroupInput>(() =>
    group ? { name: group.name, description: group.description, quotas: group.quotas, permissions: group.permissions, isDefault: group.isDefault } : blank(),
  );
  const [unlimited, setUnlimited] = useState(form.quotas.monthlyBudgetUsd === null);
  const perm = <K extends keyof Permissions>(k: K, v: Permissions[K]) => setForm((f) => ({ ...f, permissions: { ...f.permissions, [k]: v } }));
  const quota = (k: keyof GroupInput["quotas"], v: number | null) => setForm((f) => ({ ...f, quotas: { ...f.quotas, [k]: v } }));

  const done = () => {
    qc.invalidateQueries({ queryKey: ["groups"] });
    qc.invalidateQueries({ queryKey: ["users"] });
    onClose();
  };
  const save = useMutation({
    mutationFn: () => {
      const json = { ...form, quotas: { ...form.quotas, monthlyBudgetUsd: unlimited ? null : form.quotas.monthlyBudgetUsd } };
      return group ? unwrap(api.admin.groups[":id"].$put({ param: { id: group.id }, json })) : unwrap(api.admin.groups.$post({ json }));
    },
    onSuccess: done,
  });
  const remove = useMutation({ mutationFn: () => unwrap(api.admin.groups[":id"].$delete({ param: { id: group!.id } })), onSuccess: done });

  return (
    <Dialog open onClose={onClose} title={group ? `Edit ${group.name}` : "New group"} wide>
      <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name"><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Description"><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
        </div>

        <section>
          <h3 className="mb-3 text-sm font-semibold">Budget & resources</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Monthly LLM budget per user (USD)" hint="Requests are refused once a user reaches this.">
              <div className="flex items-center gap-3">
                <Input type="number" min={0} step="0.01" disabled={unlimited} value={form.quotas.monthlyBudgetUsd ?? 0} onChange={(e) => quota("monthlyBudgetUsd", Number(e.target.value))} />
                <label className="flex shrink-0 items-center gap-2 text-sm"><input type="checkbox" checked={unlimited} onChange={(e) => setUnlimited(e.target.checked)} />Unlimited</label>
              </div>
            </Field>
            <Field label="Storage per user (GB)" hint="Size of the user's private disk.">
              <Input type="number" min={1} max={1024} value={form.quotas.storageGb} onChange={(e) => quota("storageGb", Number(e.target.value))} />
            </Field>
            <Field label="Assistant memory (MB)" hint="Hard limit for the agent process.">
              <Input type="number" min={256} max={16384} step={128} value={form.quotas.memoryMb} onChange={(e) => quota("memoryMb", Number(e.target.value))} />
            </Field>
            <Field label="Sandbox memory (MB)" hint="Hard limit for commands the agent runs. An overflow kills the command, not the assistant.">
              <Input type="number" min={256} max={16384} step={128} value={form.quotas.sandboxMemoryMb} onChange={(e) => quota("sandboxMemoryMb", Number(e.target.value))} />
            </Field>
          </div>
        </section>

        <section>
          <h3 className="mb-1 text-sm font-semibold">Capabilities</h3>
          <div className="grid gap-x-8 sm:grid-cols-2">
            {toggles.map((t) => (
              <Switch
                key={t.key}
                label={t.label}
                hint={t.later ? `${t.hint} · applies ${t.later}` : t.hint}
                checked={form.permissions[t.key] as boolean}
                onChange={(v) => perm(t.key, v as never)}
              />
            ))}
          </div>
        </section>

        <section>
          <h3 className="mb-1 text-sm font-semibold">Command-line tools</h3>
          <p className="mb-3 text-xs text-[var(--muted)]">Binaries available inside the user's sandbox; the rest are deleted when it starts. Requires Terminal.</p>
          <div className="flex flex-wrap gap-2">
            {CLI_TOOLS.map((tool) => {
              const on = form.permissions.cliTools.includes(tool);
              return (
                <button
                  type="button"
                  key={tool}
                  disabled={!form.permissions.terminal}
                  onClick={() => perm("cliTools", on ? form.permissions.cliTools.filter((x) => x !== tool) : [...form.permissions.cliTools, tool])}
                  className={cx("rounded-lg border px-3 py-1.5 font-mono text-xs transition disabled:opacity-40", on ? "border-[var(--color-accent)] bg-sky-500/10" : "border-[var(--line)] hover:bg-[var(--panel-2)]")}
                >
                  {tool}
                </button>
              );
            })}
          </div>
        </section>

        <ToolkitPicker value={form.permissions.composioToolkits} onChange={(v) => perm("composioToolkits", v)} />

        <section>
          <h3 className="mb-1 text-sm font-semibold">Chat apps <span className="font-normal text-[var(--muted)]">· members can talk to their assistant from these (set up under Messaging)</span></h3>
          <div className="grid gap-x-8 sm:grid-cols-3">
            {([["slack", "Slack"], ["discord", "Discord"], ["whatsapp", "WhatsApp"]] as const).map(([m, label]) => (
              <Switch key={m} label={label} checked={form.permissions.messaging[m]} onChange={(v) => perm("messaging", { ...form.permissions.messaging, [m]: v })} />
            ))}
          </div>
        </section>

        <Switch label="Default group" hint="New users join this group automatically." checked={form.isDefault} disabled={group?.isDefault} onChange={(v) => setForm({ ...form, isDefault: v })} />

        <ErrorText error={save.error ?? remove.error} />
        <div className="flex items-center justify-between gap-2 border-t border-[var(--line)] pt-5">
          {group && !group.isDefault ? (
            <Button type="button" variant="danger" onClick={() => confirm(`Delete ${group.name}? Its members move to the default group.`) && remove.mutate()}>Delete group</Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={save.isPending}>Save</Button>
          </div>
        </div>
      </form>
    </Dialog>
  );
}

/** Composio toolkits a group may connect: none, a chosen list, or everything ("*"). */
function ToolkitPicker({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const mode = value.includes("*") ? "all" : value.length ? "some" : "none";
  const [q, setQ] = useState("");
  const search = useQuery({
    queryKey: ["admin-toolkits", q],
    queryFn: () => unwrap(api.admin.composio.toolkits.$get({ query: { q } })),
    enabled: mode === "some",
    staleTime: 60_000,
  });
  const setMode = (m: string) => onChange(m === "all" ? ["*"] : m === "none" ? [] : value.filter((v) => v !== "*").length ? value.filter((v) => v !== "*") : ["gmail", "googlecalendar", "github"]);
  return (
    <section>
      <h3 className="mb-1 text-sm font-semibold">Connected apps</h3>
      <p className="mb-3 text-xs text-[var(--muted)]">Apps members may connect their own accounts to (via Composio). Each user's connections are private to them.</p>
      {search.data && !search.data.enabled && <p className="mb-2 text-xs text-amber-700">Composio isn't configured on this deployment, so this has no effect yet.</p>}
      <div className="mb-3 flex gap-2">
        {([["none", "Off"], ["some", "Selected apps"], ["all", "All apps"]] as const).map(([m, label]) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={cx("rounded-lg border px-3 py-1.5 text-sm transition", mode === m ? "border-[var(--color-accent)] bg-sky-500/10" : "border-[var(--line)] hover:bg-[var(--panel-2)]")}
          >
            {label}
          </button>
        ))}
      </div>
      {mode === "some" && (
        <>
          <div className="mb-2 flex flex-wrap gap-1.5">
            {value.map((slug) => (
              <button key={slug} type="button" onClick={() => onChange(value.filter((v) => v !== slug))} className="rounded-md bg-sky-500/10 px-2 py-1 font-mono text-xs text-sky-800 hover:line-through" title="Remove">
                {slug} ×
              </button>
            ))}
          </div>
          <Input placeholder="Search apps to add (e.g. notion, slack, hubspot)" value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="mt-2 flex max-h-40 flex-wrap gap-1.5 overflow-y-auto">
            {search.data?.toolkits
              .filter((t) => !value.includes(t.slug))
              .map((t) => (
                <button key={t.slug} type="button" onClick={() => onChange([...value, t.slug])} className="flex items-center gap-1.5 rounded-lg border border-[var(--line)] px-2 py-1 text-xs hover:bg-[var(--panel-2)]">
                  {t.logo && <img src={t.logo} alt="" className="h-4 w-4 object-contain" />}
                  {t.name}
                </button>
              ))}
          </div>
        </>
      )}
    </section>
  );
}
